import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  setSearchQuery,
  setSearchSelectedId,
  setSearchPage,
  getSearchState,
  fetchProjectGroupsWithProjects,
} from "src/features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import { SearchInput } from "src/common/components/SearchInput";
import { PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";

export type SearchItem = {
  id: number | string;
  name: string;
  type: "projectGroup" | "project";
  projectGroupId: number | string;
};

const ProjectGroupSearchBar = ({
  className,
  isFilteredByPublishedProjects = false,
  disabled,
}: {
  className?: string;
  isFilteredByPublishedProjects?: boolean;
  disabled?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { limit, sort, query: reduxQuery } = useSelector(getSearchState);
  const [localQuery, setLocalQuery] = useState("");
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local input with Redux when query is cleared externally (e.g. sort change)
  useEffect(() => {
    setLocalQuery(reduxQuery);
  }, [reduxQuery]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setLocalQuery(value);
    dispatch(setSearchQuery(value));

    // Cancel previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // When clearing, dispatch immediately without debounce
    if (!value.trim()) {
      dispatch(setSearchSelectedId(0));
      dispatch(setSearchPage(0));
      dispatch(
        fetchProjectGroupsWithProjects({
          start: 0,
          limit,
          sort,
          query: undefined,
          filterByPublishedProject: isFilteredByPublishedProjects,
          componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
        }),
      );
      return;
    }

    // Debounce the server request (200ms)
    debounceRef.current = setTimeout(() => {
      dispatch(setSearchSelectedId(0));
      dispatch(setSearchPage(0));
      dispatch(
        fetchProjectGroupsWithProjects({
          start: 0,
          limit,
          sort,
          query: value.trim(),
          filterByPublishedProject: isFilteredByPublishedProjects,
          componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
        }),
      );
    }, 200);
  };

  return (
    <div className={`search-filter-project-groups ${className ?? ""}`} style={{ width: 250 }}>
      <SearchInput
        value={localQuery}
        onChange={handleSearchChange}
        placeholder={`${t("general.search")}...`}
        disabled={disabled}
        className="w-full"
      />
    </div>
  );
};

export default ProjectGroupSearchBar;
